import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  InputLabel,
  useTheme,
  Grid,
  Avatar,
  Alert,
  alpha,
  Tooltip,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel
} from '@mui/x-data-grid'
import {
  PersonAdd,
  MoreVert,
  Edit,
  Delete,
  Block,
  People,
  AdminPanelSettings,
  SupportAgent,
  Person,
  Email,
  ConfirmationNumber
} from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { userService } from '../../../../shared/services/UserService'
import { OrganizationService } from '../../../../shared/services/OrganizationService'
import { UserSummary, InvitationSummary, Organization } from '../../../../shared/types/entities'
import { InviteUserDialog } from '../../components/user/InviteUserDialog'

interface EditUserData {
  firstName: string
  lastName: string
  email: string
  role: 'Customer' | 'Agent' | 'Admin' | 'Oversight'
}

// Combined type for both users and invitations in the grid
type UserOrInvitation = (UserSummary & { type: 'user' }) | (InvitationSummary & { type: 'invitation' })

const editUserSchema = yup.object({
  firstName: yup.string().required('First name is required').min(2, 'At least 2 characters'),
  lastName: yup.string().required('Last name is required').min(2, 'At least 2 characters'),
  email: yup.string().required('Email is required').email('Invalid email address'),
  role: yup.string().required('Role is required').oneOf(['Customer', 'Agent', 'Admin', 'Oversight'] as const)
})

function UserManagement() {
  const theme = useTheme()
  const [users, setUsers] = useState<UserOrInvitation[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'lastName', sort: 'asc' }
  ])
  const [selectedSiteId, setSelectedSiteId] = useState<string>('')
  const [selectedOversightSiteIds, setSelectedOversightSiteIds] = useState<string[]>([])

  // Dialog states
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null)
  const [selectedRow, setSelectedRow] = useState<UserOrInvitation | null>(null)
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch
  } = useForm<EditUserData>({
    resolver: yupResolver(editUserSchema),
    defaultValues: { role: 'Agent' }
  })

  const selectedRole = watch('role')

  useEffect(() => {
    loadUsers()
    loadOrganizations()
  }, [])

  const loadUsers = async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch both active users and pending invitations
      const [usersResponse, invitationsResponse] = await Promise.all([
        userService.getUsers(1, 100),
        userService.getPendingInvitations()
      ])

      // Show all users (Customers, Agents, Admins, Oversight) with all statuses
      const activeUsers: UserOrInvitation[] = usersResponse.data
        .map(user => ({
          ...user,
          type: 'user' as const,
          id: user.userId // DataGrid requires an 'id' field
        }))

      // Add all pending invitations
      const pendingInvitations: UserOrInvitation[] = invitationsResponse
        .map(inv => ({
          ...inv,
          type: 'invitation' as const,
          id: inv.invitationId,
          // Add placeholder fields for grid columns
          isActive: false,
          emailVerified: false,
          assignedTicketCount: 0,
          openTicketCount: 0,
          resolvedTicketCount: 0,
          closedTicketCount: 0
        }))

      // Combine and sort: active users first, then invitations
      setUsers([...activeUsers, ...pendingInvitations])
    } catch (err: any) {
      setError(err.message || 'Failed to load users')
      console.error('Failed to load users:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadOrganizations = async () => {
    try {
      const orgs = await OrganizationService.getOrganizations()
      setOrganizations(orgs)
    } catch (err: any) {
      console.error('Failed to load organizations:', err)
      // Don't show error to user, just log it
    }
  }

  const handleInviteSuccess = () => {
    setSuccessMessage('Invitation sent successfully')
    setTimeout(() => setSuccessMessage(null), 3000)
    loadUsers()
  }

  const handleUserAction = async (action: string, row: UserOrInvitation) => {
    setActionMenuAnchor(null)
    setSelectedUser(row.type === 'user' ? row : null)

    try {
      setError(null)
      switch (action) {
        case 'edit':
          if (row.type === 'user') {
            // Set the selected site ID based on user's current assignment
            const userSiteId = row.childSiteId || row.organizationId || ''
            setSelectedSiteId(userSiteId)
            // Set oversight child site IDs if user is Oversight role
            setSelectedOversightSiteIds(row.oversightChildSiteIds || [])
            // Reset form with user's current data
            reset({
              firstName: row.firstName,
              lastName: row.lastName,
              email: row.email,
              role: row.role as 'Customer' | 'Agent' | 'Admin' | 'Oversight'
            })
            setEditDialogOpen(true)
          }
          break
        case 'resend':
          if (window.confirm(`Resend invitation email to ${row.email}?`)) {
            await userService.resendInvitation(row.email)
            setSuccessMessage(`Invitation resent to ${row.email}`)
            setTimeout(() => setSuccessMessage(null), 3000)
          }
          break
        case 'suspend':
          if (row.type === 'user') {
            if (row.isActive) {
              await userService.deactivateUser(row.userId)
              setSuccessMessage(`User ${row.email} suspended successfully`)
            } else {
              await userService.updateUser(row.userId, {
                firstName: row.firstName,
                lastName: row.lastName,
                role: row.role,
                isActive: true
              })
              setSuccessMessage(`User ${row.email} reactivated successfully`)
            }
            setTimeout(() => setSuccessMessage(null), 3000)
            loadUsers()
          }
          break
        case 'delete':
          if (row.type === 'user') {
            if (window.confirm(`Are you sure you want to permanently delete user ${row.email}? This action cannot be undone.`)) {
              await userService.deleteUser(row.userId)
              setSuccessMessage(`User ${row.email} deleted successfully`)
              setTimeout(() => setSuccessMessage(null), 3000)
              loadUsers()
            }
          }
          break
      }
    } catch (err: any) {
      setError(err.message || 'Failed to perform action')
      console.error('Failed to perform user action:', err)
    }
  }

  const handleEditUser = async (data: EditUserData) => {
    if (!selectedUser) return

    try {
      setError(null)

      // Determine childSiteId and organizationId from selected site
      const selectedOrg = selectedSiteId ? organizations.find(o =>
        o.childSiteId === selectedSiteId || o.organizationId === selectedSiteId
      ) : null

      await userService.updateUser(selectedUser.userId, {
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role,
        isActive: selectedUser.isActive,
        childSiteId: selectedOrg?.childSiteId || null,
        organizationId: selectedOrg?.organizationId || null,
        oversightChildSiteIds: data.role === 'Oversight' ? selectedOversightSiteIds : undefined
      })
      setSuccessMessage(`User ${selectedUser.email} updated successfully`)
      setTimeout(() => setSuccessMessage(null), 3000)
      setEditDialogOpen(false)
      setSelectedUser(null)
      setSelectedSiteId('')
      setSelectedOversightSiteIds([])
      reset() // Clear form after successful update
      loadUsers()
    } catch (err: any) {
      setError(err.message || 'Failed to update user')
      console.error('Failed to update user:', err)
    }
  }

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false)
    setSelectedUser(null)
    setSelectedSiteId('')
    setSelectedOversightSiteIds([])
    reset() // Clear form when closing
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Admin': return <AdminPanelSettings fontSize="small" />
      case 'Agent': return <SupportAgent fontSize="small" />
      case 'Customer': return <Person fontSize="small" />
      case 'Oversight': return <People fontSize="small" />
      default: return <Person fontSize="small" />
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Admin': return 'error'
      case 'Agent': return 'primary'
      case 'Customer': return 'success'
      case 'Oversight': return 'info'
      default: return 'default'
    }
  }

  const getStatusLabel = (row: UserOrInvitation) => {
    if (row.type === 'invitation') {
      return row.isExpired ? 'Expired' : 'Pending Invite'
    }
    if (!row.isActive) return 'Inactive'
    if (!row.emailVerified) return 'Pending Verification'
    if (!row.lastLoginAt) return 'Not Logged In'
    return 'Active'
  }

  const getUserInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const columns: GridColDef[] = [
    {
      field: 'fullName',
      headerName: 'User',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams<UserOrInvitation>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 40, height: 40 }}>
            {getUserInitials(params.row.firstName, params.row.lastName)}
          </Avatar>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }} noWrap>
              {params.row.firstName} {params.row.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {params.row.email}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'site',
      headerName: 'Site/Organisation',
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<UserOrInvitation>) => {
        // For invitations, show organisationName
        if (params.row.type === 'invitation') {
          const orgName = params.row.organisationName
          return (
            <Typography variant="body2" noWrap>
              {orgName || '-'}
            </Typography>
          )
        }
        // For users, show childSiteName or organizationName
        const childSite = params.row.childSiteName
        const org = params.row.organizationName
        return (
          <Typography variant="body2" noWrap>
            {childSite || org || '-'}
          </Typography>
        )
      }
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params: GridRenderCellParams<UserOrInvitation>) => (
        <Chip
          icon={getRoleIcon(params.row.role)}
          label={params.row.role}
          color={getRoleColor(params.row.role) as any}
          size="small"
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams<UserOrInvitation>) => {
        const status = getStatusLabel(params.row)
        const color = status === 'Pending Invite' || status === 'Pending Verification' || status === 'Not Logged In' ? 'warning' :
                      status === 'Expired' || status === 'Inactive' ? 'error' :
                      'success'
        return (
          <Chip
            label={status}
            color={color as any}
            size="small"
          />
        )
      }
    },
    {
      field: 'assignedTicketCount',
      headerName: 'Assigned Tickets',
      type: 'number',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<UserOrInvitation>) => (
        <Tooltip title={`${params.row.assignedTicketCount} total tickets assigned`}>
          <Chip
            icon={<ConfirmationNumber fontSize="small" />}
            label={params.row.assignedTicketCount}
            size="small"
            variant="outlined"
            sx={{ minWidth: 60 }}
          />
        </Tooltip>
      )
    },
    {
      field: 'openTicketCount',
      headerName: 'Open Tickets',
      type: 'number',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<UserOrInvitation>) => (
        <Chip
          label={params.row.openTicketCount || 0}
          size="small"
          color={(params.row.openTicketCount || 0) > 0 ? 'error' : 'default'}
          sx={{ minWidth: 45 }}
        />
      )
    },
    {
      field: 'resolvedTicketCount',
      headerName: 'Resolved Tickets',
      type: 'number',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<UserOrInvitation>) => (
        <Chip
          label={params.row.resolvedTicketCount}
          size="small"
          color="success"
          variant="outlined"
          sx={{ minWidth: 45 }}
        />
      )
    },
    {
      field: 'closedTicketCount',
      headerName: 'Closed Tickets',
      type: 'number',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<UserOrInvitation>) => (
        <Chip
          label={params.row.closedTicketCount}
          size="small"
          color="success"
          sx={{ minWidth: 45 }}
        />
      )
    },
    {
      field: 'lastLoginAt',
      headerName: 'Last Login',
      width: 150,
      renderCell: (params: GridRenderCellParams<UserOrInvitation>) => {
        if (params.row.type === 'invitation') {
          const invitedDate = new Date(params.row.invitedAt)
          return (
            <Typography variant="body2" color="text.secondary">
              Invited {invitedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </Typography>
          )
        }
        return (
          <Typography variant="body2">
            {params.row.lastLoginAt ? new Date(params.row.lastLoginAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }) : 'Never'}
          </Typography>
        )
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams<UserOrInvitation>) => (
        <IconButton
          onClick={(e) => {
            setActionMenuAnchor(e.currentTarget)
            setSelectedRow(params.row)
            setSelectedUser(params.row.type === 'user' ? params.row : null)
          }}
        >
          <MoreVert />
        </IconButton>
      )
    }
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <People sx={{ color: theme.palette.primary.main }} />
            User Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage all users across the system - agents, administrators, customers, and oversight users
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setInviteDialogOpen(true)}
          sx={{ px: 3 }}
        >
          Invite User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Users DataGrid */}
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={users}
          columns={columns}
          loading={loading}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } }
          }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04)
              }
            },
            '& .MuiDataGrid-cell': {
              py: 1
            }
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        {selectedRow?.type === 'user' && (
          <MenuItem onClick={() => handleUserAction('edit', selectedRow!)}>
            <Edit sx={{ mr: 1 }} fontSize="small" />
            Edit User
          </MenuItem>
        )}
        {selectedRow && ((selectedRow.type === 'user' && selectedRow.isActive && (!selectedRow.lastLoginAt || !selectedRow.emailVerified)) || selectedRow.type === 'invitation') && (
          <MenuItem onClick={() => handleUserAction('resend', selectedRow!)}>
            <Email sx={{ mr: 1 }} fontSize="small" />
            {selectedRow.type === 'user' && selectedRow.lastLoginAt ? 'Resend Verification' : 'Resend Invite'}
          </MenuItem>
        )}
        {selectedRow?.type === 'user' && (
          <MenuItem onClick={() => handleUserAction('suspend', selectedRow!)}>
            <Block sx={{ mr: 1 }} fontSize="small" />
            {selectedRow?.isActive ? 'Suspend' : 'Reactivate'}
          </MenuItem>
        )}
        {selectedRow?.type === 'user' && (
          <MenuItem onClick={() => handleUserAction('delete', selectedRow!)} sx={{ color: 'error.main' }}>
            <Delete sx={{ mr: 1 }} fontSize="small" />
            Delete User
          </MenuItem>
        )}
      </Menu>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={handleInviteSuccess}
        availableRoles={['Customer', 'Agent', 'Admin', 'Oversight']}
        childSites={organizations}
      />

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <form onSubmit={handleSubmit(handleEditUser)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  {...register('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  {...register('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  {...register('email')}
                  InputProps={{
                    readOnly: true,
                  }}
                  helperText="Email cannot be changed"
                  sx={{ '& .MuiInputBase-input': { backgroundColor: '#f5f5f5' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.role}>
                  <InputLabel>Role</InputLabel>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Role"
                      >
                        <MenuItem value="Customer">Customer</MenuItem>
                        <MenuItem value="Agent">Agent</MenuItem>
                        <MenuItem value="Admin">Admin</MenuItem>
                        <MenuItem value="Oversight">Oversight</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.role && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors.role.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {selectedRole === 'Oversight' && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Oversight Sites</InputLabel>
                    <Select
                      multiple
                      value={selectedOversightSiteIds}
                      onChange={(e) => setSelectedOversightSiteIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                      input={<OutlinedInput label="Oversight Sites" />}
                      renderValue={(selected) => {
                        const selectedNames = organizations
                          .filter(org => selected.includes(org.childSiteId || org.organizationId || ''))
                          .map(org => org.name)
                        return selectedNames.join(', ')
                      }}
                    >
                      {organizations.filter(org => org.childSiteId).map((org) => (
                        <MenuItem
                          key={org.childSiteId}
                          value={org.childSiteId}
                        >
                          <Checkbox checked={selectedOversightSiteIds.includes(org.childSiteId!)} />
                          <ListItemText primary={org.name} />
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                      Select which sites this Oversight user can access
                    </Typography>
                  </FormControl>
                </Grid>
              )}
              {selectedRole !== 'Oversight' && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Site/Organisation</InputLabel>
                    <Select
                      value={selectedSiteId}
                      onChange={(e) => setSelectedSiteId(e.target.value)}
                      label="Site/Organisation"
                    >
                      <MenuItem value="">
                        <em>No Site/Organisation</em>
                      </MenuItem>
                      {organizations.map((org) => (
                        <MenuItem
                          key={org.childSiteId || org.organizationId}
                          value={org.childSiteId || org.organizationId}
                        >
                          {org.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button type="submit" variant="contained">Save Changes</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  )
}

export default UserManagement